import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { LocaleEnum } from '@systemeio/ui'
import {
  AutomationsIcon,
  BlogsIcon,
  ContactsIcon,
  DashboardIcon,
  EmailsIcon,
  HelpIcon,
  ManInSuit,
  ProductsIcon,
  SalesIcon,
  SmsMenuIcon,
  UpgradeIcon,
} from '@systemeio/ui/icons'
import { isClient, isSioHost } from '@systemeio/util'
import { GrowthbookFlagsEnum } from 'growthbook/types'
import { AdminRolesEnum } from 'shared/enums/user-roles-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { formUrl } from 'shared/utils/links/form-url'
import { MenuItemType } from '../types/menu-interface'
import { useRouter } from 'next/router'

export const useMenuItems = () => {
  const { t } = useLocoTranslation()
  const {
    checkAdminRoles,
    user,
    isWorkspaceOwner,
    isSubAccountImpersonation,
    isSupportImpersonation,
    isWsAssistant,
    isCustomer,
    isAffiliate,
    isStudent,
    isWsAdmin,
  } = useUser()
  const websiteFeatureIsOn = useFeatureIsOn(GrowthbookFlagsEnum.Website)
  const whiteLabelFeatureIsOn = useFeatureIsOn(GrowthbookFlagsEnum.WhiteLabel)

  const isRegularAdmin = checkAdminRoles([
    AdminRolesEnum.support,
    AdminRolesEnum.admin,
    AdminRolesEnum.superAdmin,
  ])

  const { pathname } = useRouter()

  const regularIsActiveCheck = (url: string) => pathname.includes(url)

  const menuItems: MenuItemType[] = [
    {
      href: '/profile/user-plan',
      shouldRender: user?.userPlanId === 40 && isWorkspaceOwner && !isSupportImpersonation,
      mobileIconRenderer: () => <UpgradeIcon />,
      name: t('header.top_menu.upgrade'),
      isActive: regularIsActiveCheck,
    },
    {
      name: t('header.top_menu.dashboard'),
      dropdownItems: [
        {
          name: t('header.top_menu.main_dashboard'),
          href: '/',
          shouldRender: (isWorkspaceOwner || isWsAdmin) && isCustomer,
          isActive: url => pathname === url,
        },
        {
          name: t('header.top_menu.affiliate_dashboard'),
          href: '/affiliate-dashboard',
          shouldRender: isWorkspaceOwner && isAffiliate,
          isActive: regularIsActiveCheck,
        },
        {
          name: t('header.top_menu.student_dashboard'),
          shouldRender: isStudent,
          href: isStudent
            ? formUrl('/school', false) ?? ''
            : formUrl('/school?tab=explore_courses', false) ?? '',
          isActive: regularIsActiveCheck,
        },
        {
          name: t('header.top_menu.agency_dashboard'),
          href: '/agency-center/white-label-users',
          shouldRender: (() => {
            if (!whiteLabelFeatureIsOn) return false
            if (isSubAccountImpersonation) return false
            if (!isCustomer) return false
            if (!isWorkspaceOwner) return false
            if (!isClient()) return false
            return isSioHost(window.location.host)
          })(),
          isActive: () => pathname.includes('/agency-center'),
        },
      ],
      mobileIconRenderer: () => <DashboardIcon />,
    },

    {
      name: t('header.top_menu.crm'),
      dropdownItems: [
        {
          name: t('header.top_menu.contacts'),
          href: isWsAssistant ? '/assistant/contacts' : '/contacts',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
        {
          name: t('header.top_menu.tags'),
          href: '/tags',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },

        {
          name: t('header.top_menu.crm_pipeline'),
          href: '/crm-pipeline',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
        {
          name: t('header.top_menu.calendar'),
          href: '/calendar',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
      ],
      mobileIconRenderer: () => <ContactsIcon />,
    },

    {
      name: t('header.top_menu.sites'),
      dropdownItems: [
        {
          name: t('header.top_menu.website'),
          href: '/websites',
          shouldRender: isCustomer && websiteFeatureIsOn,
          isActive: regularIsActiveCheck,
        },
        {
          name: t('header.top_menu.funnels'),
          href: '/funnels',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
        {
          name: t('header.top_menu.digital_stores'),
          href: '/stores',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
        {
          name: t('header.top_menu.blogs'),
          href: '/blogs',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
      ],
      mobileIconRenderer: () => <BlogsIcon />,
    },

    {
      name: t('header.top_menu.emails'),
      dropdownItems: [
        {
          name: t('header.top_menu.newsletters'),
          href: '/newsletters',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
        {
          name: t('header.top_menu.campaigns'),
          href: '/campaigns',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
        {
          name: t('header.top_menu.email_statistics'),
          href: '/email-statistics',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
      ],
      mobileIconRenderer: () => <EmailsIcon />,
    },

    {
      name: t('header.top_menu.sms'),
      dropdownItems: [
        {
          name: t('header.top_menu.sms_templates'),
          href: '/sms/templates',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
        {
          name: t('header.top_menu.sms_statistics'),
          href: '/sms/statistics',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
      ],
      mobileIconRenderer: () => <SmsMenuIcon />,
    },
    {
      name: t('header.top_menu.automations'),
      dropdownItems: [
        {
          name: t('header.top_menu.rules'),
          href: '/automation-rules',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
        {
          name: t('header.top_menu.workflows'),
          href: '/workflows',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
      ],
      mobileIconRenderer: () => <AutomationsIcon />,
    },

    {
      name: t('header.top_menu.products'),
      dropdownItems: [
        {
          name: t('header.top_menu.physical_product'),
          href: '/products',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
        {
          name: t('header.top_menu.coupons'),
          href: '/coupons',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
        {
          name: t('header.top_menu.courses'),
          href: '/courses',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
        {
          name: t('header.top_menu.communities'),
          href: '/communities',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
        {
          name: t('header.top_menu.files'),
          href: '/files',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
      ],
      mobileIconRenderer: () => <ProductsIcon />,
    },

    {
      name: t('header.top_menu.sales'),
      dropdownItems: [
        {
          name: t('header.top_menu.orders'),
          href: '/orders',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
        {
          name: t('header.top_menu.transactions'),
          href: '/payment/transactions',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
        {
          name: t('header.top_menu.subscriptions'),
          href: '/payment/subscriptions',
          isActive: regularIsActiveCheck,
          shouldRender: isCustomer,
        },
        {
          name: t('header.top_menu.affiliate_invoices'),
          href: '/payment/affiliate-invoices',
          shouldRender: (() => {
            if (isWsAssistant) return false
            if (!isClient()) return false
            return isSioHost(window.location.host)
          })(),
          isActive: regularIsActiveCheck,
        },
        {
          name: t('header.top_menu.affiliate_program'),
          href: '/affiliate-program',
          shouldRender: (() => {
            if (!isWorkspaceOwner) return false
            if (!isClient()) return false
            return isSioHost(window.location.host)
          })(),
          isActive: regularIsActiveCheck,
        },
      ],
      mobileIconRenderer: () => <SalesIcon />,
    },

    {
      name: t('header.top_menu.admin.admin'),
      dropdownItems: [
        {
          name: t('header.top_menu.admin.users'),
          href: '/management/users',
          shouldRender: isRegularAdmin,
          isActive: regularIsActiveCheck,
        },
        {
          name: t('header.top_menu.admin.templates'),
          dropdownItems: [
            {
              name: t('header.top_menu.admin.email_templates'),
              href: '/management/email-templates',
              isActive: regularIsActiveCheck,
              shouldRender: checkAdminRoles([
                AdminRolesEnum.contentManager,
                AdminRolesEnum.admin,
                AdminRolesEnum.superAdmin,
              ]),
            },
            {
              name: t('header.top_menu.admin.page_templates'),
              href: '/management/page-templates',
              isActive: regularIsActiveCheck,
              shouldRender: checkAdminRoles([
                AdminRolesEnum.contentManager,
                AdminRolesEnum.admin,
                AdminRolesEnum.superAdmin,
              ]),
            },
            {
              name: t('header.top_menu.admin.blog_templates'),
              href: '/management/blog-templates',
              isActive: regularIsActiveCheck,
              shouldRender: checkAdminRoles([
                AdminRolesEnum.contentManager,
                AdminRolesEnum.admin,
                AdminRolesEnum.superAdmin,
              ]),
            },
            {
              name: t('header.top_menu.admin.website_templates'),
              href: '/management/website-templates',
              shouldRender:
                checkAdminRoles([
                  AdminRolesEnum.contentManager,
                  AdminRolesEnum.admin,
                  AdminRolesEnum.superAdmin,
                ]) && websiteFeatureIsOn,
              isActive: regularIsActiveCheck,
            },
            {
              name: t('header.top_menu.admin.funnel_templates'),
              href: '/management/funnel-templates',
              isActive: regularIsActiveCheck,
              shouldRender: checkAdminRoles([
                AdminRolesEnum.contentManager,
                AdminRolesEnum.admin,
                AdminRolesEnum.superAdmin,
              ]),
            },
          ],
        },
        {
          name: t('header.top_menu.admin.customers'),
          href: '/management/user-customer-list',
          shouldRender: checkAdminRoles([
            AdminRolesEnum.mailingManager,
            AdminRolesEnum.admin,
            AdminRolesEnum.superAdmin,
            AdminRolesEnum.support,
          ]),
          isActive: regularIsActiveCheck,
        },
        {
          name: t('header.top_menu.admin.emails'),
          href: '/management/emails-delivery-statistics',
          shouldRender: isRegularAdmin,
          isActive: regularIsActiveCheck,
        },
        {
          name: t('header.top_menu.admin.user_plans'),
          href: '/management/user-plans',
          shouldRender: !isWsAssistant && isRegularAdmin,
          isActive: regularIsActiveCheck,
        },
        {
          name: t('header.top_menu.admin.domains_blocked'),
          href: '/management/blocked-mailbox-provider',
          shouldRender: isRegularAdmin,
          isActive: regularIsActiveCheck,
        },
        {
          name: t('header.top_menu.admin.domains_white_listed'),
          href: '/management/white-list-mailbox',
          shouldRender: isRegularAdmin,
          isActive: regularIsActiveCheck,
        },
        {
          name: t('header.top_menu.admin.suspicious_content'),
          dropdownItems: [
            {
              name: t('header.top_menu.admin.suspicious_page_content_keywords'),
              href: '/management/suspicious-page-content-keyword',
              isActive: regularIsActiveCheck,
              shouldRender: isRegularAdmin,
            },
            {
              name: t('header.top_menu.admin.suspicious_email_keywords'),
              href: '/management/suspicious-email-keyword',
              isActive: regularIsActiveCheck,
              shouldRender: isRegularAdmin,
            },
            {
              name: t('header.top_menu.admin.suspicious_email_address'),
              href: '/management/suspicious-email-address',
              isActive: regularIsActiveCheck,
              shouldRender: isRegularAdmin,
            },
          ],
        },
        {
          name: t('header.top_menu.admin.statistics'),
          href: '/management/statistics',
          shouldRender:
            (isWorkspaceOwner || isWsAdmin) && checkAdminRoles([AdminRolesEnum.superAdmin]),
          isActive: regularIsActiveCheck,
        },
      ],
      mobileIconRenderer: () => <ManInSuit />,
    },
    {
      name: t('header.top_menu.help'),
      href:
        user?.dashboardLocale === LocaleEnum.ENGLISH
          ? `${process.env.NEXT_PUBLIC_SIO_HOST_SCHEME}://${process.env.NEXT_PUBLIC_SIO_HOST_NAME}/help`
          : `${process.env.NEXT_PUBLIC_SIO_HOST_SCHEME}://${process.env.NEXT_PUBLIC_SIO_HOST_NAME}/${user?.dashboardLocale}/help`,
      target: '__blank',
      mobileIconRenderer: () => <HelpIcon />,
      shouldRender: isClient() ? isSioHost(window.location.host) : false,
      isActive: regularIsActiveCheck,
    },
  ]

  return { menuItems }
}
